/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  RECORDING_DENIED,
  RECORDING_SESSION_ALREADY_FINALIZED,
  RECORDING_SESSION_CHUNK_UPLOAD_FAILED,
  RECORDING_STARTED,
  RECORDING_STARTING,
  RECORDING_STOPPED,
  RECORDING_STOPPING,
  RECORDING_UPLOADED,
  UPDATE_RECORDING_DURATION,
} from "constants/ActionTypes";

import { RecorderUploadStates } from "constants/RecorderUploadStates";
import { ClipId } from "types/Clip";
import { RecorderAction } from "types/Actions";

export enum RecorderStatus {
  RECORDING_STARTING = "RECORDING_STARTING",
  RECORDING_STOPPING = "RECORDING_STOPPING",
  RECORDING_STARTED = "RECORDING_STARTED",
  RECORDING_STOPPED = "RECORDING_STOPPED",
}

export interface RecorderState {
  status: RecorderStatus;
  duration?: number;
  recordingSessionId?: string;
  clipId?: ClipId;
  uploadState?: RecorderUploadStates;
  denied?: boolean;
  chunkIssues?: boolean;
}

export default function RecorderReducer(
  state: RecorderState,
  action: RecorderAction
): RecorderState {
  switch (action.type) {
    case RECORDING_STARTING:
      return {
        ...state,
        status: RecorderStatus.RECORDING_STARTING,
        duration: 0,
        recordingSessionId: action.recordingSessionId,
        clipId: action.clipId,
        uploadState: RecorderUploadStates.PENDING,
        denied: false,
        chunkIssues: false,
      };
    case RECORDING_STOPPING:
      return {
        ...state,
        status: RecorderStatus.RECORDING_STOPPING,
      };
    case RECORDING_STARTED:
      return {
        ...state,
        status: RecorderStatus.RECORDING_STARTED,
      };
    case RECORDING_SESSION_ALREADY_FINALIZED:
      return {
        ...state,
        status: RecorderStatus.RECORDING_STOPPED,
        uploadState: RecorderUploadStates.COMPLETE,
      };
    case RECORDING_STOPPED:
      return {
        ...state,
        status: RecorderStatus.RECORDING_STOPPED,
        uploadState: RecorderUploadStates.STARTED,
      };
    case UPDATE_RECORDING_DURATION:
      return {
        ...state,
        duration: action.duration,
      };
    case RECORDING_UPLOADED:
      return {
        ...state,
        uploadState: RecorderUploadStates.COMPLETE,
      };
    case RECORDING_DENIED:
      return {
        ...state,
        status: RecorderStatus.RECORDING_STOPPED,
        denied: true,
      };
    case RECORDING_SESSION_CHUNK_UPLOAD_FAILED:
      return {
        ...state,
        chunkIssues: true,
      };
    default:
      return state;
  }
}
